import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";

/**
 * Animated coupled-window ("kopplat fönster") explainer.
 * Sequence: closed -> inner sash swings open -> middle glass highlighted -> label appears.
 * Hover/tap the highlighted surface reveals the caption.
 */
const AnimatedWindow = () => {
  const [step, setStep] = useState(0);
  const [tapped, setTapped] = useState(false);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStep(1), 700),   // sash opens
      setTimeout(() => setStep(2), 1500),  // middle glass highlighted
      setTimeout(() => setStep(3), 2200),  // label appears
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const active = step >= 2 || tapped;

  return (
    <div className="rounded-2xl bg-brand-iceBg p-5" data-testid="mellan-illustration">
      <div className="relative mx-auto max-w-[300px]" style={{ perspective: "900px" }}>
        <svg viewBox="0 0 300 210" className="w-full h-auto" role="img" aria-label="Animerad illustration av kopplat fönster">
          {/* Outer frame */}
          <rect x="14" y="14" width="272" height="182" rx="8" fill="#E2E8F0" />
          <rect x="26" y="26" width="248" height="158" fill="#BFDBFE" />
          {/* Middle glass surface (the add-on area) */}
          <rect
            x="38" y="38" width="224" height="134" rx="3"
            fill="#0E62FE"
            className="transition-all duration-700"
            fillOpacity={active ? 0.45 : 0.08}
            onMouseEnter={() => setTapped(true)}
            onMouseLeave={() => setTapped(false)}
            onClick={() => setTapped((v) => !v)}
            style={{ cursor: "pointer" }}
            data-testid="mellan-glass-hotspot"
          />
          {/* Inner sash — swings open on a left hinge */}
          <g
            className="transition-transform duration-1000"
            style={{
              transformBox: "fill-box",
              transformOrigin: "left center",
              transform: step >= 1 ? "rotateY(-48deg)" : "rotateY(0deg)",
              pointerEvents: "none",
            }}
          >
            <rect x="48" y="48" width="204" height="114" rx="4" fill="#F8FAFC" stroke="#94A3B8" strokeWidth="2.5" />
            <rect x="60" y="60" width="180" height="90" fill="#EFF6FF" />
            <line x1="150" y1="60" x2="150" y2="150" stroke="#CBD5E1" strokeWidth="4" />
          </g>
        </svg>
        {/* Label */}
        <AnimatePresence>
          {step >= 3 && (
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-blue text-white text-xs font-semibold px-4 py-1.5 shadow-[0_8px_20px_rgba(14,98,254,0.35)]"
            >
              Mellan fönstren
            </motion.span>
          )}
        </AnimatePresence>
      </div>
      <p className="mt-3 text-center text-sm font-medium text-brand-blue min-h-[20px]" aria-live="polite">
        {tapped ? "Glaset mellan fönsterbågarna" : step >= 2 ? "Tryck på den markerade glasytan" : "Så här sitter ett kopplat fönster…"}
      </p>
    </div>
  );
};

export const MellanModal = ({ open, onAdd, onDecline }) => {
  useEffect(() => {
    if (!open) return;
    window.__lenis?.stop();
    const onKey = (e) => { if (e.key === "Escape") onDecline(); };
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onDecline]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div className="absolute inset-0 bg-brand-ink/45 backdrop-blur-md" onClick={onDecline} data-testid="mellan-backdrop" />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="mellan-title"
            data-testid="mellan-modal"
            initial={{ y: 60, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 360, damping: 32 }}
            className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-[0_40px_80px_-20px_rgba(7,22,44,0.4)] p-6 md:p-8 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[85vh] sm:max-h-[92vh] overflow-y-auto"
          >
            <button
              onClick={onDecline}
              aria-label="Stäng"
              data-testid="mellan-close"
              className="absolute top-4 right-4 h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4 text-slate-600" />
            </button>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-blue">Tillval</p>
            <h3 id="mellan-title" className="mt-2 font-heading text-2xl md:text-3xl font-bold text-brand-ink tracking-tight">
              Har du kopplade fönster?
            </h3>
            <p className="mt-2.5 text-slate-600 leading-relaxed">
              Vill du även putsa <strong className="text-brand-ink">mellan fönstren</strong>? Putsning av glasytorna mellan fönsterbågarna på kopplade eller fyrsidiga fönster.
            </p>
            <div className="mt-5">
              <AnimatedWindow />
            </div>
            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={onAdd}
                data-testid="mellan-add"
                autoFocus
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue text-white font-semibold px-6 py-4 min-h-[52px] hover:bg-brand-blueHover hover:shadow-[0_10px_25px_rgba(14,98,254,0.3)] active:scale-[0.99] transition-all"
              >
                <Check className="h-4 w-4" strokeWidth={2.5} /> Ja, lägg till Mellan fönstren
              </button>
              <button
                onClick={onDecline}
                data-testid="mellan-decline"
                className="w-full rounded-full bg-white text-brand-ink border border-slate-200 font-medium px-6 py-4 min-h-[52px] hover:border-brand-blue hover:text-brand-blue transition-colors"
              >
                Nej tack, fortsätt
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MellanModal;
